<template>
  <c-box>
    <c-box
      :border-radius="{ lg: 'base' }"
      background="white"
      :box-shadow="{ lg: 'base' }"
      :px="{ base: 'md', sm: '2xl' }"
      :py="{ base: 'md', sm: '3xl' }"
      :mx="{ base: '-31px', md: '-45px', lg: '0' }"
      word-wrap="break-word"
      data-cy="shopping-cart-section"
    >
      <table v-chakra text-align="left" width="100%" :mb="{ lg: '2xl' }">
        <thead>
          <tr
            v-chakra="
              (theme) => ({
                borderBottomWidth: theme.border.base,
                borderColor: 'gray.300',
                fontWeight: 'normal',
                th: {
                  fontWeight: 'normal',
                  pb: 'lg',
                },
                display: {
                  base: 'none',
                  lg: 'table-row',
                },
              })
            "
          >
            <th>{{ $t("shoppingCart.heading.product") }}</th>
            <th>{{ $t("shoppingCart.heading.productId") }}</th>
            <th>{{ $t("shoppingCart.heading.quantity") }}</th>
            <th>{{ $t("shoppingCart.heading.unitPrice") }}</th>
            <th>{{ $t("shoppingCart.heading.totalPrice") }}</th>
            <!-- action column for trashcan -->
            <th></th>
          </tr>
        </thead>

        <tbody>
          <ShoppingCartItem
            v-for="cartItem in cartItems"
            :key="cartItem.product.id"
            :cartItem="cartItem"
            :showProduct="showProduct"
            :removeFromCart="removeFromCart"
            :checkAvailability="checkingAvailability"
          />
        </tbody>
      </table>

      <c-flex :align="{ md: 'center' }" :flex-direction="{ base: 'column-reverse', md: 'row' }">
        <!-- quick search -->
        <c-flex align="center" flex="1">
          <c-icon name="plus" size="2.25rem" mr="md" color="brand.purple" />
          <c-box width="100%" max-width="24rem">
            <QuickProductSearch @product-picked="productPickedFromQuickSearch"></QuickProductSearch>
          </c-box>
        </c-flex>
        <!-- total price -->
        <c-box align="right" :mb="{ base: 'xs', md: '0' }">
          <c-text as="span" font-weight="bold">{{ $t("shoppingCart.totalPrice") }}</c-text>
          <Price
            v-if="orderTotalSum && orderTotalSum.currencyCode && orderTotalSum.price > 0"
            :price="orderTotalSum.price"
            :currency="orderTotalSum.currencyCode"
            align="right"
          />
        </c-box>
      </c-flex>
    </c-box>
    <c-box
      :border-radius="{ lg: 'base' }"
      background="white"
      :box-shadow="{ lg: 'base' }"
      :px="{ base: 'md', sm: '2xl' }"
      :py="{ base: 'md', sm: '3xl' }"
      :mx="{ base: '-31px', md: '-45px', lg: '0' }"
      mt="3xl"
      word-wrap="break-word"
      data-cy="project-id-section"
    >
      <c-form-control :is-invalid="inputIsInvalid('projectDiscountCode', true)" mb="0">
        <c-text font-weight="bold"> {{ $t("ordering.projectDetails.projectId") }}</c-text>
        <c-radio-group v-model="projectIdDisabled" my="xs">
          <c-radio id="useProjectId" name="projectIdRadio" value=""
            ><c-form-label for="useProjectId">{{
              $t("ordering.projectDetails.useProjectId")
            }}</c-form-label></c-radio
          >
        </c-radio-group>
        <c-flex
          :flex-direction="{ base: 'column', md: 'row' }"
          align-items="baseline"
          :gap="{ base: 'sm', md: 'xl' }"
        >
          <c-box :width="{ base: '100%', md: 'auto' }" :ml="{ base: '', md: 'lg' }">
            <c-flex
              v-if="projectIdSelected"
              border-radius="input"
              border="1px solid"
              border-color="inherit"
              px="4"
              height="12"
              font-size="1rem !important"
              width="100%"
              justify-content="space-between"
              align-items="center"
              :min-width="{ md: '9rem', lg: '16rem', xl: '24rem' }"
              bg="brand.blueLightest"
            >
              <c-text as="span" class="name">{{ projectDiscountCode }}</c-text>
              <button
                class="delete"
                @click.prevent="clearActiveProject()"
                data-cy="project-id-clear-selected"
              >
                <c-icon name="trashcan" size="1.5rem" />
              </button>
            </c-flex>
            <c-input
              v-else
              id="projectId"
              :disabled="projectIdDisabled === '1'"
              :placeholder="$t('ordering.projectDetails.projectIdInputDescription')"
              size="lg"
              v-model.trim="projectDiscountCode"
              data-cy="project-id-input"
              type="text"
              :min-width="{ md: '16rem', lg: '24rem' }"
            />
            <c-box
              v-if="isErrorField('projectDiscountCode')"
              color="brand.red"
              font-size="sm"
              my="xs"
              data-cy="project-id-invalid"
            >
              <span>
                {{
                  $t("common.error.fieldInvalid", {
                    field: $t("ordering.projectDetails.projectId"),
                  })
                }}
              </span>
            </c-box>
          </c-box>
          <c-box>
            <c-button
              variation="solid"
              @click="onProjectIdApplied()"
              :disabled="projectIdDisabled === '1'"
              white-space="normal"
              data-cy="project-id-apply-button"
              mr="auto"
              mb="2xs"
            >
              {{ $t("common.apply") }}
            </c-button>
          </c-box>
          <c-box flex="1" position="relative" :ml="{ '2xl': '4xl' }">
            <Spinner v-if="projectLoading" size="md" v-chakra mb="-2xs" />
            <c-box v-else-if="projectError">
              <c-text>
                {{ $t("ordering.projectDetails.projectIdError") }}
              </c-text>
            </c-box>
            <c-box v-else-if="activeProjectHasDiscounts()" data-cy="project-discount-applied">
              <c-text>
                {{ $t("ordering.projectDetails.projectDiscountApplied") }}:
                <c-text v-if="activeProject.percentagePointDiscount" as="span" font-weight="bold">
                  <br />{{
                    $t("ordering.projectDetails.projectDiscountApplied.percentagePointDiscount")
                  }}
                  -{{ activeProject.percentagePointDiscount || 0 }}%
                </c-text>
                <c-text v-else-if="activeProject.onlineDiscount" as="span" font-weight="bold">
                  <br />{{ $t("ordering.projectDetails.projectDiscountApplied.hardware") }} -{{
                    activeProject.discount || 0
                  }}%, {{ $t("ordering.projectDetails.projectDiscountApplied.online") }} -{{
                    activeProject.onlineDiscount
                  }}%<template v-if="(activeProject.netPrices || []).length > 0"
                    ><br />
                    {{ $t("ordering.projectDetails.projectDiscountApplied.netPrices") }}
                  </template>
                </c-text>
                <c-text v-else-if="activeProject.discount" as="span" font-weight="bold">
                  -{{ activeProject.discount }}%<template
                    v-if="(activeProject.netPrices || []).length > 0"
                    ><br />
                    {{ $t("ordering.projectDetails.projectDiscountApplied.netPrices") }}
                  </template>
                </c-text>
                <c-text
                  v-else-if="(activeProject.netPrices || []).length > 0"
                  as="span"
                  font-weight="bold"
                >
                  {{ $t("ordering.projectDetails.projectDiscountApplied.netPrices") }}
                </c-text>
                <c-text v-else as="span" font-weight="bold">-</c-text>
              </c-text>
              <c-text>
                {{ $t("ordering.projectDetails.projectOwner") }}:
                <c-text as="span" font-weight="bold">
                  {{ activeProject.owner }}
                </c-text>
              </c-text>
            </c-box>
            <c-box v-else-if="projectIdSelected" data-cy="project-id-not-found">
              <c-text v-html="$t('ordering.projectDetails.projectIdNotFoundNote')" />
            </c-box>
          </c-box>
        </c-flex>
        <c-radio-group v-model="projectIdDisabled" my="xs">
          <c-radio id="disableProjectId" name="projectIdRadio" value="1"
            ><c-form-label for="disableProjectId">
              {{ $t("ordering.projectDetails.noProjectNote") }}
            </c-form-label></c-radio
          >
        </c-radio-group>
      </c-form-control>
    </c-box>
  </c-box>
</template>

<script lang="ts">
import { defineComponent } from "vue";
import {
  IOrderRow,
  IPrice,
  IPartnerProductSearchResultValue,
  rules,
  IDiscounts,
  IRootState,
} from "@iloq/shared";
import QuickProductSearch from "@/components/products/QuickProductSearch.vue";
import { productToOrderRow } from "../util/ProductUtil";
import Price from "@/design-system/elements/Price.vue";
import ShoppingCartItem from "./ordering/ShoppingCartItem.vue";
import Button from "../design-system/elements/Button.vue";
import { mapState } from "vuex";
import {
  CIcon,
  CBox,
  CFlex,
  CText,
  CHeading,
  CFormControl,
  CFormLabel,
  CInput,
  CButton,
  CRadioGroup,
  CRadio,
} from "@chakra-ui/vue";
import Spinner from "@/components/Spinner.vue";
import { inputMixin } from "./ordering/OrderMixins";
import { Logger } from "@iloq/shared";

export default defineComponent({
  name: "ShoppingCart",
  components: {
    QuickProductSearch,
    Price,
    ShoppingCartItem,
    Button,
    CIcon,
    CBox,
    CFlex,
    CText,
    CHeading,
    CFormControl,
    CFormLabel,
    CInput,
    CButton,
    CRadioGroup,
    CRadio,
    Spinner,
  },
  props: {
    checkingAvailability: {
      type: Boolean,
      default: false,
    },
    onFieldBlur: {
      type: Function,
      default: () => {},
    },
    onFieldChange: {
      type: Function,
      default: () => {},
    },
    isErrorField: {
      type: Function,
      default: () => {},
    },
    getFieldClasses: {
      type: Function,
      default: () => {},
    },
    cart: {
      type: Object,
      default: () => {},
    },
  },
  mixins: [inputMixin],
  data(): any {
    const showProductSpecificMaxOverLimitError = false;
    return {
      showProductSpecificMaxOverLimitError,
      projectIdSelected: false,
      projectIdDisabled: "",
      projectDiscountCode: "",
    };
  },
  computed: {
    cartItems(): IOrderRow[] {
      const serviceproductIds: string[] = this.$store.state.productData.serviceproducts.ids;

      return this.$store.state.shoppingCart.cartRows.filter((row: IOrderRow) => {
        return !serviceproductIds.includes(row.product.id);
      });
    },
    orderTotalSum(): IPrice {
      return this.$store.state.shoppingCart.totalSum;
    },
    ...mapState("project", {
      projectList: "list",
      projectLoading: "loading",
      projectError: "error",
    }),
    ...mapState({
      partnerId: (state: unknown) => (state as IRootState).user?.partnerId,
      activeProject: (state: unknown) => (state as IRootState).shoppingCart?.discounts || {},
      currentProjectIdStatus: (state: unknown) =>
        (state as IRootState).shoppingCart?.projectIdDisabled || "",
    }),
    // ...mapGetters("project", {
    //   projectFromList: "projectFromList",
    // }),
  },
  mounted(): void {
    this.projectDiscountCode = this.activeProject.projectDiscountCode || "";
    this.projectIdDisabled = this.currentProjectIdStatus;
    this.$emit("set-project-id-disabled", this.projectIdDisabled);
    if (this.projectDiscountCode) {
      this.projectIdSelected = true;
      this.$emit("set-project-id-selected", this.projectIdSelected);
    }
  },
  methods: {
    removeFromCart(orderRow: IOrderRow): void {
      this.$store.dispatch("shoppingCart/removeFromCart", orderRow);
    },
    showProduct(id: string): void {
      this.$router.push(`/products/${id}`);
    },
    productPickedFromQuickSearch(product: IPartnerProductSearchResultValue): void {
      const cartItems: IOrderRow[] = this.$store.state.shoppingCart.cartRows;
      const existingQuickSearchItem = cartItems.find((item) => item.product.id === product.sku);
      if (
        existingQuickSearchItem?.product.maxOrderQuantity &&
        Number(existingQuickSearchItem.quantity) ===
          Number(existingQuickSearchItem.product.maxOrderQuantity)
      ) {
        return;
      }
      this.$store.dispatch("shoppingCart/addToCart", productToOrderRow(product));
    },
    async onProjectIdDisabled(): Promise<void> {
      if (this.projectIdDisabled) await this.clearActiveProject();
      this.$emit("set-project-id-disabled", this.projectIdDisabled);
      try {
        this.$store.dispatch("shoppingCart/setProjectIdDisabled", this.projectIdDisabled);
      } catch (error) {
        Logger.error(error);
      }
    },
    async onProjectIdApplied(): Promise<void> {
      this.$emit("set-project-discount-code", this.projectDiscountCode);
      if (this.isErrorField("projectDiscountCode")) {
        return;
      }

      try {
        const discounts: IDiscounts = await this.$store.dispatch(
          "project/fetchDiscounts",
          this.projectDiscountCode,
          { root: true },
        );
        this.$store.dispatch("shoppingCart/setDiscounts", discounts);
        if (discounts.projectDiscountCode) {
          this.projectIdSelected = true;
          this.$emit("set-project-id-selected", this.projectIdSelected);
        } else {
          this.projectDiscountCode = undefined;
          this.projectIdSelected = false;
        }
      } catch (error) {
        Logger.error(error);
      }
    },
    async clearActiveProject(): Promise<void> {
      this.projectIdSelected = false;
      this.projectDiscountCode = "";
      this.$emit("set-project-discount-code", "");
      this.$emit("set-project-id-selected", this.projectIdSelected);
      this.$store.dispatch("shoppingCart/unsetDiscounts");
    },
    activeProjectHasDiscounts(): boolean {
      return (
        (this.activeProject.discount || 0) > 0 ||
        (this.activeProject.onlineDiscount || 0) > 0 ||
        (this.activeProject.netPrices || []).length > 0 ||
        (this.activeProject.percentagePointDiscount || 0) > 0 ||
        this.activeProject.discountsRequireManualProcessing
      );
    },
  },
  watch: {
    projectDiscountCode(): void {
      if (
        this.projectDiscountCode === "" ||
        (this.projectDiscountCode.length >= rules.minLength.global.projectId &&
          this.projectDiscountCode.length <= rules.maxLength.global.projectId)
      ) {
        this.$emit("set-project-discount-code", this.projectDiscountCode);
      }
    },
    projectIdDisabled: function () {
      this.onProjectIdDisabled();
    },
  },
});
</script>
